import React from 'react';

import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash-es';
import { Link, useHistory } from 'react-router-dom';

import Breadcrumbs from 'components/Breadcrumbs';
import { useFeatures, FeatureFlags } from 'contexts/features';
import {
  CHARGING_STATIONS_POLICIES_AND_GROUPS_CHARGING_GROUPS_ROUTE,
  CHARGING_STATIONS_POLICIES_AND_GROUPS_CHARGING_POLICIES_ROUTE,
  CHARGING_STATIONS_POLICIES_AND_GROUPS_CUSTOMER_GROUPS_ROUTE,
  CHARGING_STATIONS_POLICIES_AND_GROUPS_EDIT_CHARGING_POLICY_ROUTE,
  CHARGING_STATIONS_POLICIES_AND_GROUPS_NEW_CHARGING_POLICY_ROUTE,
  CHARGING_STATIONS_ROUTE,
  PAYMENT_TERMINALS_ROUTE,
  SYSTEM_PAYMENT_TERMINALS_ROUTE,
  SYSTEM_CHARGING_STATIONS_ROUTE,
} from '../router/routes';

export function CpoBreadcrumbs() {
  const history = useHistory();
  const { t } = useTranslation();
  const { hasFeature } = useFeatures();

  const pathnameWithoutId = removeIdFromPath(history.location.pathname);

  const breadcrumbs = [];
  switch (pathnameWithoutId) {
    case CHARGING_STATIONS_POLICIES_AND_GROUPS_CHARGING_POLICIES_ROUTE:
    case CHARGING_STATIONS_POLICIES_AND_GROUPS_CUSTOMER_GROUPS_ROUTE:
    case CHARGING_STATIONS_POLICIES_AND_GROUPS_CHARGING_GROUPS_ROUTE:
      if (hasFeature(FeatureFlags.PoliciesAndGroups)) {
        breadcrumbs.push(
          ...[
            {
              path: CHARGING_STATIONS_ROUTE,
              title: t('chargingStations.title', 'Charging Stations'),
            },
            {
              path: CHARGING_STATIONS_POLICIES_AND_GROUPS_CHARGING_POLICIES_ROUTE,
              title: t('policiesAndGroups.title', 'Policies & Groups'),
            },
          ]
        );
      }
      break;
    case CHARGING_STATIONS_POLICIES_AND_GROUPS_EDIT_CHARGING_POLICY_ROUTE:
      if (hasFeature(FeatureFlags.PoliciesAndGroups)) {
        breadcrumbs.push(
          ...[
            {
              path: CHARGING_STATIONS_ROUTE,
              title: t('chargingStations.title', 'Charging Stations'),
            },
            {
              path: CHARGING_STATIONS_POLICIES_AND_GROUPS_CHARGING_POLICIES_ROUTE,
              title: t('policiesAndGroups.title', 'Policies & Groups'),
            },
            {
              path: CHARGING_STATIONS_POLICIES_AND_GROUPS_NEW_CHARGING_POLICY_ROUTE,
              title: t('chargingPolicyForm.title.edit', 'Edit Charging Policy'),
            },
          ]
        );
      }
      break;
    case CHARGING_STATIONS_POLICIES_AND_GROUPS_NEW_CHARGING_POLICY_ROUTE:
      if (hasFeature(FeatureFlags.PoliciesAndGroups)) {
        breadcrumbs.push(
          ...[
            {
              path: CHARGING_STATIONS_ROUTE,
              title: t('chargingStations.title', 'Charging Stations'),
            },
            {
              path: CHARGING_STATIONS_POLICIES_AND_GROUPS_CHARGING_POLICIES_ROUTE,
              title: t('policiesAndGroups.title', 'Policies & Groups'),
            },
            {
              path: CHARGING_STATIONS_POLICIES_AND_GROUPS_NEW_CHARGING_POLICY_ROUTE,
              title: t(
                'chargingPolicyForm.title.create',
                'New Charging Policy'
              ),
            },
          ]
        );
      }
      break;
    case PAYMENT_TERMINALS_ROUTE:
      breadcrumbs.push(
        ...[
          {
            path: CHARGING_STATIONS_ROUTE,
            title: t('chargingStations.title', 'Charging Stations'),
          },
          {
            path: PAYMENT_TERMINALS_ROUTE,
            title: t('paymentTerminals.title', 'Payment Terminals'),
          },
        ]
      );
      break;
    case SYSTEM_PAYMENT_TERMINALS_ROUTE:
      breadcrumbs.push(
        ...[
          {
            path: SYSTEM_CHARGING_STATIONS_ROUTE,
            title: t('chargingStations.title', 'Charging Stations'),
          },
          {
            path: SYSTEM_PAYMENT_TERMINALS_ROUTE,
            title: t('systemPaymentTerminals.title', 'Payment Terminals'),
          },
        ]
      );
      break;
  }

  if (isEmpty(breadcrumbs)) {
    return null;
  }

  return (
    <Breadcrumbs
      path={breadcrumbs.slice(0, -1).map((breadcrumb) => (
        <Link key={breadcrumb.path} to={breadcrumb.path}>
          {breadcrumb.title}
        </Link>
      ))}
      active={breadcrumbs[breadcrumbs.length - 1].title}
    />
  );
}

/**
 * We need to remove ids from here with a regex because we don't have access to the params
 * from the react-router-dom library in here
 */
function removeIdFromPath(path: string) {
  const uuidRegex =
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
  const objectIdRegex = /[0-9a-f]{24}/gi;
  return path.replace(uuidRegex, ':id').replace(objectIdRegex, ':id');
}
